import { ActionIcon, Button } from '@mantine/core';
import { Icon, sizes, toastNotifications, variants } from '@uag/react-core';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Rnd } from 'react-rnd';
import { useShallow } from 'zustand/shallow';

import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { getMostDistinctPastelColorHex, hexToPastelHue, pastelHueToHex } from 'app/modules/wallforming/components/utils/pastelUtils';

import type { GroupMember } from 'app/modules/wallforming/components/scenes/objects/articles/types/groupMember';

const modalWidth = 340;
const modalHeight = 370;

export const CreateGroupModal = () => {
    const { t } = useTranslation();

    const {
        isGroupPanelOpen,
        editingGroupId,
        articleInstanceSelections,
        articles,
        pendingGroupName,
        setPendingGroupName,
        pendingGroupColor,
        setPendingGroupColor,
        createArticleGroup,
        updateArticleGroup,
        setIsGroupPanelOpen,
        articleGroups
    } = usePlacementStore(
        useShallow((state) => ({
            isGroupPanelOpen: state.isGroupPanelOpen,
            editingGroupId: state.editingGroupId,
            articleInstanceSelections: state.articleInstanceSelections,
            articles: state.articles,
            pendingGroupName: state.pendingGroupName,
            setPendingGroupName: state.setPendingGroupName,
            pendingGroupColor: state.pendingGroupColor,
            setPendingGroupColor: state.setPendingGroupColor,
            createArticleGroup: state.createArticleGroup,
            updateArticleGroup: state.updateArticleGroup,
            setIsGroupPanelOpen: state.setIsGroupPanelOpen,
            articleGroups: state.articleGroups
        }))
    );

    const isEditMode = editingGroupId !== null;

    const selectedInstanceIds = useMemo(() => {
        return Object.keys(articleInstanceSelections);
    }, [articleInstanceSelections]);

    const members: GroupMember[] = useMemo(() => {
        const result: GroupMember[] = [];
        for (const [articleId, article] of Object.entries(articles)) {
            for (const instanceId of Object.keys(article.articleInstances)) {
                if (selectedInstanceIds.includes(instanceId)) {
                    result.push({ articleId, instanceId });
                }
            }
        }
        return result;
    }, [articles, selectedInstanceIds]);

    const memberSummary = useMemo(() => {
        const byArticle = new Map<string, number>();
        for (const member of members) {
            const article = articles[member.articleId];
            const label = article?.articleNumber ?? member.articleId;
            byArticle.set(label, (byArticle.get(label) ?? 0) + 1);
        }
        return Array.from(byArticle.entries()).map(([articleNumber, count]) => ({ articleNumber, count }));
    }, [members, articles]);

    const groupCount = useMemo(() => Object.keys(articleGroups).length, [articleGroups]);

    const pastelHue = useMemo(
        () =>
            pendingGroupColor.startsWith('#') && pendingGroupColor.length === 7 ? hexToPastelHue(pendingGroupColor) : 0,
        [pendingGroupColor]
    );

    const isGroupNameEmpty = useMemo(() => pendingGroupName.trim().length === 0, [pendingGroupName]);

    const canCreate = useMemo(() => members.length >= 2 && !isGroupNameEmpty, [members, isGroupNameEmpty]);

    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleCreateGroup = useCallback(() => {
        if (!canCreate) {
            toastNotifications.information({ title: t('info'), message: t('infoCreateGroup') });
            return;
        }

        if (isEditMode && editingGroupId) {
            updateArticleGroup(editingGroupId, pendingGroupName.trim(), members, pendingGroupColor);
        } else {
            createArticleGroup(pendingGroupName.trim(), members, pendingGroupColor);
        }
        setIsGroupPanelOpen(false);
    }, [
        canCreate,
        createArticleGroup,
        editingGroupId,
        isEditMode,
        members,
        pendingGroupColor,
        pendingGroupName,
        setIsGroupPanelOpen,
        t,
        updateArticleGroup
    ]);

    const handleCloseModal = useCallback(() => {
        setIsGroupPanelOpen(false);
    }, [setIsGroupPanelOpen]);

    const handleSetFocusOnInput = useCallback(() => {
        if (isGroupPanelOpen) {
            requestAnimationFrame(() => {
                const input = inputRef.current;
                if (input) {
                    input.focus();
                    input.setSelectionRange(input.value.length, input.value.length);
                }
            });
        }
    }, [isGroupPanelOpen]);

    const handleCreateGroupOnEnter = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && canCreate) {
                handleCreateGroup();
            }
        },
        [handleCreateGroup, canCreate]
    );

    useEffect(() => {
        handleSetFocusOnInput();
    }, [handleSetFocusOnInput]);

    useEffect(() => {
        if (!isGroupPanelOpen) {
            setPendingGroupName('');
            return;
        }
        if (!isEditMode) {
            setPendingGroupName(`Group ${groupCount + 1}`);
            const existingColors = Object.values(articleGroups).map((group) => group.color);
            setPendingGroupColor(getMostDistinctPastelColorHex(existingColors));
        }
    }, [groupCount, isEditMode, isGroupPanelOpen, setPendingGroupName, setPendingGroupColor, articleGroups]);

    return (
        <div
            style={{
                height: '100vh',
                left: 0,
                pointerEvents: 'none',
                position: 'fixed',
                top: 0,
                width: '100vw',
                zIndex: 9999,
                visibility: isGroupPanelOpen ? 'visible' : 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerMove={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
        >
            <Rnd
                bounds="window"
                default={{
                    x: Math.max((window.innerWidth - modalWidth) / 2, 0),
                    y: Math.max((window.innerHeight - modalHeight) / 2, 0),
                    width: modalWidth,
                    height: modalHeight
                }}
                dragHandleClassName="rnd-drag-handle"
                enableResizing={false}
                enableUserSelectHack={false}
                size={{ width: 340, height: 370 }}
                style={{ pointerEvents: 'auto' }}
            >
                {/* Panel */}
                <div
                    style={{
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 6,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        color: '#374151',
                        fontFamily: 'inherit',
                        fontSize: 12,
                        userSelect: 'none',
                        width: 340,
                        height: 370,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Header */}
                    <div
                        className="rnd-drag-handle"
                        style={{
                            alignItems: 'center',
                            background: '#f3f4f6',
                            borderBottom: '1px solid #e5e7eb',
                            borderRadius: '6px 6px 0 0',
                            cursor: 'move',
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '6px 10px'
                        }}
                    >
                        <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.5 }}>
                            {isEditMode ? t('labelEditGroup', 'Edit Group') : t('labelCreateGroup')}
                        </span>

                        <ActionIcon
                            className="action-icon-small bg-background-gray-ultra-light hover:bg-background-gray! pointer-events-auto icon-sm"
                            onClick={handleCloseModal}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            close
                        </ActionIcon>
                    </div>

                    {/* Body */}
                    <div
                        style={{
                            padding: '10px 10px 8px',
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {/* Group name and color */}
                        <div style={{ marginBottom: 10 }}>
                            <span className="text-xs font-medium text-text-light uppercase">
                                {t('labelGroupName')}
                                {isGroupNameEmpty && (
                                    <span className="normal-case" style={{ color: '#f59e0b', marginLeft: 6 }}>
                                        <Icon
                                            className="action-menu-entry-decoration action-menu-entry-decoration-icon mr-0.5"
                                            icon="warning"
                                        />
                                        {t('infoRequired')}
                                    </span>
                                )}
                            </span>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 3,
                                    marginTop: 4,
                                    marginBottom: 10
                                }}
                            >
                                <input
                                    ref={inputRef}
                                    placeholder={`Group ${groupCount + 1}`}
                                    style={{
                                        border: '1px solid #d1d5db',
                                        boxSizing: 'border-box',
                                        color: '#111827',
                                        fontSize: 12,
                                        outline: 'none',
                                        padding: '4px 8px',
                                        height: 28,
                                        borderRadius: 4,
                                        flex: 1,
                                        minWidth: 0
                                    }}
                                    type="text"
                                    value={pendingGroupName}
                                    onBlur={handleSetFocusOnInput}
                                    onChange={(e) => setPendingGroupName(e.target.value)}
                                    onKeyDown={handleCreateGroupOnEnter}
                                />
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <div
                                        style={{
                                            background: pendingGroupColor,
                                            border: '1px solid #d1d5db',
                                            borderRadius: 4,
                                            flexShrink: 0,
                                            height: 28,
                                            width: 40,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}
                                        title={pendingGroupColor}
                                    />
                                </div>
                            </div>

                            {/* Pastel hue slider */}
                            <div style={{ marginTop: 8 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: 3,
                                        marginTop: 4
                                    }}
                                >
                                    <span className="text-xs font-medium text-text-light uppercase">
                                        {t('labelGroupColor')}
                                    </span>
                                    <ActionIcon
                                        className="action-icon-small bg-background-transparent! hover:bg-background-transparent! pointer-events-auto icon-sm mb-0.5"
                                        title={t('labelCopyHexValue')}
                                        onClick={() => {
                                            navigator.clipboard.writeText(pendingGroupColor);
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                    >
                                        content_copy
                                    </ActionIcon>
                                </div>

                                <input
                                    className="color-slider"
                                    max={360}
                                    min={0}
                                    step={0.5}
                                    style={{
                                        background:
                                            'linear-gradient(to right, hsl(0,65%,78%), hsl(45,65%,78%), hsl(90,65%,78%), hsl(135,65%,78%), hsl(180,65%,78%), hsl(225,65%,78%), hsl(270,65%,78%), hsl(315,65%,78%), hsl(360,65%,78%))'
                                    }}
                                    title={pendingGroupColor}
                                    type="range"
                                    value={pastelHue}
                                    onChange={(e) => setPendingGroupColor(pastelHueToHex(parseFloat(e.target.value)))}
                                    onPointerDown={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: 25 }}>
                            <span className="text-xs font-medium text-text-light uppercase">
                                {t('labelGroupMembers')} ({members.length})
                                {members.length < 2 && (
                                    <span className="normal-case" style={{ color: '#f59e0b', marginLeft: 6 }}>
                                        <Icon
                                            className="action-menu-entry-decoration action-menu-entry-decoration-icon mr-0.5"
                                            icon="warning"
                                        />
                                        {t('validationGroupMinMembers', 'min. 2 required')}
                                    </span>
                                )}
                            </span>
                            <div
                                style={{
                                    background: '#f9fafb',
                                    border: '1px solid #e5e7eb',
                                    height: 90,
                                    overflowY: 'auto',
                                    padding: '4px 8px'
                                }}
                            >
                                {memberSummary.length === 0 ? (
                                    <div
                                        style={{
                                            color: '#9ca3af',
                                            fontSize: 11,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: '100%'
                                        }}
                                    >
                                        <span>{t('labelNoSelection', 'No articles selected')}</span>
                                    </div>
                                ) : (
                                    memberSummary.map(({ articleNumber, count }) => (
                                        <div
                                            key={articleNumber}
                                            style={{
                                                color: '#374151',
                                                display: 'flex',
                                                fontSize: 11,
                                                justifyContent: 'space-between',
                                                padding: '1px 0'
                                            }}
                                        >
                                            <span>{articleNumber}</span>
                                            <span style={{ color: '#6b7280' }}>×{count}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 'auto' }}>
                            <Button
                                size={sizes.button.small}
                                variant={variants.button.secondary}
                                onClick={handleCloseModal}
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                aria-disabled={!canCreate}
                                className={`min-w-17 flex items-center justify-center
                                    ${!canCreate ? 'bg-background-disabled cursor-not-allowed text-text-disabled' : ''}
                                `}
                                size={sizes.button.small}
                                onClick={handleCreateGroup}
                            >
                                {isEditMode ? t('ok') : t('create')}
                            </Button>
                        </div>
                    </div>
                </div>
            </Rnd>
        </div>
    );
};
